"use client";

import Link from "next/link";
import Image from "next/image";
import { useId } from "react";
import { useTranslations } from "next-intl";
import { useAuth } from "@/lib/auth-context";
import ImageCarousel, { type CarouselImage } from "@/components/home/ImageCarousel";

const AVATARS = [
  "/img/review_avatars/1.png",
  "/img/review_avatars/2.png",
  "/img/review_avatars/3.png",
  "/img/review_avatars/4.png",
];

// Add/remove entries here to change what shows in the hero carousel.
const HERO_IMAGES: CarouselImage[] = [
  { src: "/video.mp4", alt: "Saiyan Gym — video teretane", video: true },
  { src: "/img/teretana_hero.webp", alt: "Saiyan Gym — bučice i Goku ilustracija" },
  { src: "/img/saiyan5.webp", alt: "Saiyan Gym — zidni mural s Gokuom i natpisom \"You will be the best or you are nothing\"" },
  { src: "/img/saiyan2.webp", alt: "Saiyan Gym — prostor teretane s spravama za trening snage" },
  { src: "/img/cjen2.webp", alt: "Saiyan Gym — ljetna ponuda Funkcionalni Gym, dnevni termin 100 €/mjesec ili neograničeno 150 €/mjesec" },
  { src: "/img/cjen1.webp", alt: "Saiyan Gym — akcija 10 treninga s osobnim trenerom za 200 €" },
];

function SocialIcons({ compact = false }: { compact?: boolean }) {
  const t = useTranslations("Hero");
  const gradientId = `ig-${useId()}`;
  const buttonSize = compact ? "h-7 w-7" : "h-9 w-9";
  const iconSize = compact ? "h-3.5 w-3.5" : "h-4 w-4";
  const gap = compact ? "gap-1.5" : "gap-2";

  return (
    <div className={`flex items-center ${gap}`}>
      {/* Google */}
      <a
        href="https://www.google.com/maps/place/saiyan+gym+FITT/@42.6595946,18.0732326,1384m/data=!3m1!1e3!4m8!3m7!1s0x134c756c62b6ec3d:0xc72b267de58c4746!8m2!3d42.6595907!4d18.0758075!9m1!1b1!16s%2Fg%2F11t71_5c0l?entry=ttu&g_ep=EgoyMDI2MDQxNC4wIKXMDSoASAFQAw%3D%3D"
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t("googleReviewsAria")}
        className={`flex ${buttonSize} items-center justify-center rounded-full border border-[#e4e4e7] bg-white transition-colors hover:bg-[#f4f4f5]`}
      >
        <svg viewBox="0 0 24 24" className={iconSize} xmlns="http://www.w3.org/2000/svg">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
      </a>
      {/* Instagram */}
      <a
        href="https://www.instagram.com/saiyan_gym_fitt/"
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t("instagramAria")}
        className={`flex ${buttonSize} items-center justify-center rounded-full border border-[#e4e4e7] bg-white transition-colors hover:bg-[#f4f4f5]`}
      >
        <svg viewBox="0 0 24 24" className={iconSize} fill="none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <radialGradient id={gradientId} cx="30%" cy="107%" r="150%">
              <stop offset="0%" stopColor="#fdf497"/>
              <stop offset="5%" stopColor="#fdf497"/>
              <stop offset="45%" stopColor="#fd5949"/>
              <stop offset="60%" stopColor="#d6249f"/>
              <stop offset="90%" stopColor="#285AEB"/>
            </radialGradient>
          </defs>
          <rect x="2" y="2" width="20" height="20" rx="5" ry="5" fill={`url(#${gradientId})`}/>
          <circle cx="12" cy="12" r="4" stroke="white" strokeWidth="1.8" fill="none"/>
          <circle cx="17.5" cy="6.5" r="1.2" fill="white"/>
        </svg>
      </a>
      {/* Facebook */}
      <a
        href="http://facebook.com/p/Saiyan-GYM-FITT-100092144478181"
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t("facebookAria")}
        className={`flex ${buttonSize} items-center justify-center rounded-full border border-[#e4e4e7] bg-white transition-colors hover:bg-[#f4f4f5]`}
      >
        <svg viewBox="0 0 24 24" className={iconSize} fill="#1877F2" xmlns="http://www.w3.org/2000/svg">
          <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.313 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.886v2.267h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"/>
        </svg>
      </a>
    </div>
  );
}

export default function HeroSection() {
  const { is_authenticated } = useAuth();
  const t = useTranslations("Hero");

  return (
    <section className="px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-6xl rounded-3xl bg-[#F9FAFB] p-8 ring-1 ring-[#E4E4E7] sm:p-10 lg:p-14">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:gap-12">

          {/* Image */}
          <div className="order-1 w-full flex-shrink-0 lg:order-2 lg:w-[45%]">
            <ImageCarousel images={HERO_IMAGES} className="aspect-square" radiusClassName="rounded-2xl" priority />
          </div>

          {/* Content — grouped as a column on desktop, flattened on mobile so each piece can be reordered */}
          <div className="contents lg:order-1 lg:flex lg:flex-1 lg:flex-col lg:gap-6">

            {/* Social proof */}
            <div className="order-2 flex flex-col gap-3 lg:order-4">

              {/* Mobile — compact single row */}
              <div className="flex flex-nowrap items-center gap-2 lg:hidden">
                <div className="flex items-center shrink-0">
                  {AVATARS.slice(0, 2).map((src, i) => (
                    <div
                      key={i}
                      className="relative h-7 w-7 rounded-full ring-2 ring-white overflow-hidden"
                      style={{ marginLeft: i === 0 ? 0 : "-8px", zIndex: 2 - i }}
                    >
                      <Image src={src} alt={t("memberAlt", { n: i + 1 })} fill className="object-cover" />
                    </div>
                  ))}
                </div>

                <div className="flex shrink-0 items-center gap-1">
                  <svg className="h-3.5 w-3.5 fill-[#fbbf24] text-[#fbbf24]" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  <span className="text-xs font-semibold text-[#18181b]">{t("ratingCompact")}</span>
                </div>

                <SocialIcons compact />
              </div>

              {/* Desktop — full row */}
              <div className="hidden lg:flex lg:flex-col lg:gap-3">
                <span className="text-sm font-semibold text-[#18181b]">{t("socialProofTitle")}</span>
                <div className="flex flex-wrap items-center gap-4">
                  {/* Avatars */}
                  <div className="flex items-center">
                    {AVATARS.map((src, i) => (
                      <div
                        key={i}
                        className="relative h-9 w-9 rounded-full ring-2 ring-white overflow-hidden"
                        style={{ marginLeft: i === 0 ? 0 : "-10px", zIndex: AVATARS.length - i }}
                      >
                        <Image src={src} alt={t("memberAlt", { n: i + 1 })} fill className="object-cover" />
                      </div>
                    ))}
                  </div>

                  {/* Rating */}
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-semibold text-[#18181b]">4.9</span>
                    <div className="flex items-center gap-0.5">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} className="h-4 w-4 fill-[#fbbf24] text-[#fbbf24]" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                    <span className="text-sm text-[#71717a]">{t("ratingReviews")}</span>
                  </div>

                  <SocialIcons />
                </div>
              </div>
            </div>

            <h1 className="order-3 text-5xl font-bold leading-[1.1] tracking-[-0.04em] text-[#18181b] text-balance sm:text-6xl lg:order-1 lg:text-7xl">
              {t("heading")}
            </h1>

            <p className="order-4 max-w-md text-base leading-relaxed text-[#18181b] lg:order-2">
              {t("descriptionLine1")}
              <br />
              {t("descriptionLine2")}
            </p>

            <div className="order-5 grid grid-cols-2 gap-3 lg:order-3 lg:grid lg:grid-cols-[repeat(2,max-content)] lg:items-center lg:gap-3">
              {!is_authenticated && (
                <Link
                  href="/register"
                  className="order-2 rounded-full bg-brand px-3 py-2.5 text-center text-sm sm:px-6 font-medium text-brand-foreground shadow-sm transition-opacity hover:opacity-90 lg:order-none"
                >
                  {t("joinCta")}
                </Link>
              )}

              <Link
                href="#clanarine"
                className="order-1 rounded-full border border-[#E4E4E7] bg-white px-3 py-2.5 text-center text-sm sm:px-6 font-medium text-[#18181b] shadow-sm transition-colors hover:bg-[#f4f4f5] lg:order-none"
              >
                {t("membershipsCta")}
              </Link>

              <Link
                href="#sprave"
                className="order-3 rounded-full border border-[#E4E4E7] bg-white px-3 py-2.5 text-center text-sm sm:px-6 font-medium text-[#18181b] shadow-sm transition-colors hover:bg-[#f4f4f5] lg:order-none"
              >
                {t("machinesCta")}
              </Link>

              <Link
                href="#kontakt"
                className="order-4 rounded-full border border-[#E4E4E7] bg-white px-3 py-2.5 text-center text-sm sm:px-6 font-medium text-[#18181b] shadow-sm transition-colors hover:bg-[#f4f4f5] lg:order-none"
              >
                {t("contactCta")}
              </Link>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
